import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { apiClient } from '@/services/api/client';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon, 
  TruckIcon, 
  CreditCardIcon, 
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface TrackedOrder {
  order_number: string;
  customer_name: string;
  order_status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  payment_status: 'paid' | 'unpaid' | 'pending';
  created_at: string;
  updated_at: string;
}

const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    pending: 'text-yellow-700 bg-yellow-50 border-yellow-200',
    processing: 'text-blue-700 bg-blue-50 border-blue-200',
    shipped: 'text-purple-700 bg-purple-50 border-purple-200',
    delivered: 'text-green-700 bg-green-50 border-green-200',
    cancelled: 'text-red-700 bg-red-50 border-red-200',
  };
  return colors[status] || 'text-gray-700 bg-gray-50 border-gray-200';
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'delivered':
      return <CheckCircleIcon className="w-5 h-5" />;
    case 'cancelled':
      return <XCircleIcon className="w-5 h-5" />;
    case 'shipped':
      return <TruckIcon className="w-5 h-5" />;
    default:
      return <ClockIcon className="w-5 h-5" />;
  }
};

const getPaymentStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    paid: 'text-green-700 bg-green-50 border-green-200',
    pending: 'text-yellow-700 bg-yellow-50 border-yellow-200',
    unpaid: 'text-red-700 bg-red-50 border-red-200',
  };
  return colors[status] || 'text-gray-700 bg-gray-50 border-gray-200';
};

export const OrderTracking = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState<TrackedOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fromQuery = searchParams.get('orderNumber');
    if (fromQuery) {
      setOrderNumber(fromQuery);
    }
  }, [searchParams]);

  const getStatusLabel = (status: string): string => t(`orderTrackingPage.status.${status}`);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!orderNumber.trim()) {
      setError(t('orderTrackingPage.orderNumberRequired'));
      return;
    }

    setLoading(true);
    setError(null);
    setOrder(null);

    try {
      const trackedOrder = await apiClient.get<TrackedOrder>(`/orders/track/${orderNumber.trim()}`);
      setOrder(trackedOrder);
    } catch (err: any) {
      setError(err.response?.data?.error || t('orderTrackingPage.orderNotFound'));
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = i18n.language.startsWith('en') ? 'en-US' : 'fr-FR';
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="order-tracking-page">
      <Navbar />
      
      <main className="order-tracking-main">
        <div className="order-tracking-inner">
          <header className="order-tracking-hero">
            <h1 className="gtitle order-tracking-title">
              <span className="gtitle-light">{t('orderTrackingPage.titleLight')}</span>
              <span className="gtitle-strong">{t('orderTrackingPage.titleStrong')}</span>
            </h1>
            <p className="order-tracking-subtitle">
              {t('orderTrackingPage.subtitle')}
            </p>
          </header>

          <div className="order-tracking-search-card">
            <form onSubmit={handleSearch} className="w-full flex justify-center">
              <div className="search-container-wrapper w-full">
                <div className="search-container">
                  <div className="search-wrapper">
                    <input
                      type="text"
                      value={orderNumber}
                      onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                      placeholder={t('orderTrackingPage.searchPlaceholder')}
                      className="search-input"
                      disabled={loading}
                      aria-label={t('orderTrackingPage.searchPlaceholder')}
                    />
                    <button 
                      className="search-button"
                      type="submit"
                      disabled={loading}
                      style={{ opacity: loading ? 0.65 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden />
                          <span className="search-button-text">{t('orderTrackingPage.searching')}</span>
                        </>
                      ) : (
                        <>
                          <span className="search-icon" aria-hidden />
                          <span className="search-button-text">{t('orderTrackingPage.search')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>

            {error && (
              <div className="order-tracking-error" role="alert">
                <div className="order-tracking-error-inner">
                  <XCircleIcon className="w-5 h-5 shrink-0 text-red-600" aria-hidden />
                  <p className="order-tracking-error-text">{error}</p>
                </div>
              </div>
            )}
          </div>

          {order && (
            <div className="order-tracking-result animate-fadeIn">
              <div className="order-tracking-result-header">
                <div className="order-tracking-result-header-inner">
                  <div>
                    <p className="order-tracking-result-order-num-label">{t('orderTrackingPage.orderNumber')}</p>
                    <h2 className="order-tracking-result-order-num">
                      {order.order_number}
                    </h2>
                    <p className="order-tracking-result-client">
                      <span className="font-semibold">{t('orderTrackingPage.clientLabel')}</span>{' '}
                      {order.customer_name}
                    </p>
                  </div>
                  <div className="order-tracking-result-status-pill">
                    {getStatusIcon(order.order_status)}
                    <span>{getStatusLabel(order.order_status)}</span>
                  </div>
                </div>
              </div>

              <div className="order-tracking-details">
                <div className="order-tracking-details-grid">
                  <div className="order-tracking-detail-card">
                    <h3 className="order-tracking-detail-label">
                      {t('orderTrackingPage.orderDate')}
                    </h3>
                    <p className="order-tracking-detail-value">{formatDate(order.created_at)}</p>
                  </div>

                  <div className="order-tracking-detail-card">
                    <h3 className="order-tracking-detail-label">
                      {t('orderTrackingPage.lastUpdate')}
                    </h3>
                    <p className="order-tracking-detail-value">{formatDate(order.updated_at)}</p>
                  </div>

                  <div className="order-tracking-detail-card">
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${
                        order.payment_status === 'paid' ? 'bg-green-100' : 
                        order.payment_status === 'pending' ? 'bg-yellow-100' : 
                        'bg-red-100'
                      }`}>
                        <CreditCardIcon className={`w-6 h-6 ${
                          order.payment_status === 'paid' ? 'text-green-600' : 
                          order.payment_status === 'pending' ? 'text-yellow-600' : 
                          'text-red-600'
                        }`} />
                      </div>
                      <h3 className="order-tracking-detail-label mb-0">
                        {t('orderTrackingPage.paymentStatus')}
                      </h3>
                    </div>
                    <div className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm border ${getPaymentStatusColor(order.payment_status)}`}>
                      {order.payment_status === 'paid' ? (
                        <>
                          <CheckCircleIcon className="w-5 h-5" />
                          <span>{t('orderTrackingPage.payment.paid')}</span>
                        </>
                      ) : order.payment_status === 'pending' ? (
                        <>
                          <ClockIcon className="w-5 h-5" />
                          <span>{t('orderTrackingPage.payment.pending')}</span>
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="w-5 h-5" />
                          <span>{t('orderTrackingPage.payment.unpaid')}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="order-tracking-detail-card">
                    <h3 className="order-tracking-detail-label">
                      {t('orderTrackingPage.orderStatus')}
                    </h3>
                    <div className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm border ${getStatusColor(order.order_status)}`}>
                      {getStatusIcon(order.order_status)}
                      <span>{getStatusLabel(order.order_status)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!order && !error && !loading && (
            <div className="order-tracking-empty">
              <div className="order-tracking-empty-icon-wrap" aria-hidden>
                <MagnifyingGlassIcon />
              </div>
              <p className="order-tracking-empty-text">
                {t('orderTrackingPage.enterToStart')}
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
